import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../common/Layout'
import { lifeEventAPI } from '../../config/lifeEventAPI'
import { getUserFirstName } from '../../utils/userUtils'
import { useDashboardState } from '../../hooks/useDashboardState'
import WelcomeBanner from '../dashboard/WelcomeBanner'
import { getUser } from '../../utils/secureStorage'

function Dashboard() {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [greeting, setGreeting] = useState('')
  const [recentStories, setRecentStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalStories: 0,
    totalLifeEvents: 0,
    thisMonth: 0,
    withMedia: 0,
    photosCount: 0,
    familyMembersCount: 0,
    timeCapsulesCount: 0
  })

  // Calculate days active based on account creation (from localStorage)
  const daysActive = useMemo(() => {
    try {
      const user = getUser()
      if (user) {
        const createdAt = user.date_joined || user.created_at
        if (createdAt) {
          const created = new Date(createdAt)
          const now = new Date()
          return Math.floor((now - created) / (1000 * 60 * 60 * 24))
        }
      }
    } catch (e) {
      console.error('Error calculating days active:', e)
    }
    return 0
  }, [])

  // Build userData object for useDashboardState hook
  const userData = useMemo(() => ({
    storiesCount: stats.totalStories,
    photosCount: stats.photosCount,
    familyMembersCount: stats.familyMembersCount,
    timeCapsulesCount: stats.timeCapsulesCount,
    wisdomPlaylistsCount: 0,
    lifeEventsCount: stats.totalLifeEvents,
    daysActive: daysActive,
    consecutiveDaysStreak: 0,
    hasCompletedFirstStory: stats.totalStories > 0,
    totalStoryViews: 0,
    achievements: []
  }), [stats, daysActive])

  // Get dashboard state based on user data
  const dashboardState = useDashboardState(userData)

  useEffect(() => {
    // Dynamic greeting based on time
    const hour = new Date().getHours()
    let timeGreeting
    if (hour < 12) timeGreeting = 'Good morning'
    else if (hour < 18) timeGreeting = 'Good afternoon'
    else timeGreeting = 'Good evening'
    
    setGreeting(timeGreeting)
    
    // Fetch recent life events and stories
    fetchRecentData()
  }, [])

  const fetchRecentData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch all life events
      const response = await lifeEventAPI.getEvents()
      // Handle both array response and paginated {results: [...]} response
      const allEvents = Array.isArray(response) ? response : (response.results || [])
      
      // Filter for stories (template 12) and get recent ones
      const storyEvents = allEvents.filter(event => 
        event.template === 12 || event.event_data?.story_type
      )
      
      // Get recent stories (last 5)
      const recent = storyEvents
        .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date))
        .slice(0, 5)
      
      setRecentStories(recent)
      
      // Calculate stats
      const thisMonth = new Date()
      thisMonth.setMonth(thisMonth.getMonth() - 1)
      
      const monthlyEvents = allEvents.filter(event => 
        new Date(event.date || event.created_at) >= thisMonth
      )
      
      const eventsWithMedia = allEvents.filter(event => 
        event.media && event.media.length > 0
      )
      
      // Count total photos across all events
      const totalPhotos = allEvents.reduce((count, event) => {
        const mediaCount = event.media_files?.length || event.media?.length || 0
        return count + mediaCount
      }, 0)

      setStats({
        totalStories: storyEvents.length,
        totalLifeEvents: allEvents.length,
        thisMonth: monthlyEvents.length,
        withMedia: eventsWithMedia.length,
        photosCount: totalPhotos,
        familyMembersCount: 0, // Will be fetched from family API if needed
        timeCapsulesCount: 0   // Will be fetched from time capsule API if needed
      })
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err)
      setError('Failed to load recent data. Please try again.')
      
      // Set mock data for development
      setRecentStories(getMockStories())
      setStats({
        totalStories: 6,
        totalLifeEvents: 12,
        thisMonth: 3,
        withMedia: 8,
        photosCount: 15,
        familyMembersCount: 4,
        timeCapsulesCount: 2
      })
    } finally {
      setLoading(false)
    }
  }

  const getMockStories = () => {
    return [
      {
        id: 1,
        title: "Grandma's Secret Cookie Recipe",
        story_type: "Family Memory",
        date: "2024-01-15",
        created_at: "2024-01-15T10:30:00Z"
      },
      {
        id: 2,
        title: "The Lost Vacation Adventure",
        story_type: "Funny Moment",
        date: "2023-12-20",
        created_at: "2023-12-20T14:15:00Z"
      },
      {
        id: 3,
        title: "Sarah's Graduation Day",
        story_type: "Celebration",
        date: "2023-11-25",
        created_at: "2023-11-25T16:30:00Z"
      }
    ]
  }

  // Helper function to get emoji based on story type
  const getStoryEmoji = (storyType) => {
    const emojiMap = {
      'Family Memory': '👨‍👩‍👧‍👦',
      'Funny Moment': '😄',
      'Celebration': '🎉',
      'Life Lesson': '💡',
      'Recipe': '🍳',
      'Tradition': '🎄',
      'Career': '💼',
      'Travel': '✈️',
      'Wedding': '💒',
      'Birth': '👶',
      'Graduation': '🎓',
      'Home': '🏠',
      'Vacation': '🏖️',
      'default': '📖'
    }
    return emojiMap[storyType] || emojiMap['default']
  }

  const handleMemoryCardClick = (memoryTitle) => {
    alert(`Opening memory: ${memoryTitle}`)
  }

  const handleFileSelect = async (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    try {
      setError(null)
      // For now, navigate to a page where they can associate photos with events
      alert(`${files.length} photo(s) selected. Navigate to a specific story or event to attach photos.`)
      navigate('/stories')
    } catch (err) {
      console.error('Error handling photo upload:', err)
      setError('Failed to process photos. Please try again.')
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleActionButton = (action) => {
    switch (action) {
      case 'record-story':
        navigate('/tell-a-story')
        break
      case 'add-family-member':
        navigate('/familytree')
        break
      case 'upload-photos':
        // Trigger the hidden file input
        if (fileInputRef.current) {
          fileInputRef.current.click()
        }
        break
      case 'share-recipe':
        alert('Opening recipe creation form...')
        break
      case 'create-wisdom-playlist':
        alert('Opening wisdom playlist creator...')
        break
      case 'answer-prompt':
        alert('Opening prompt response interface...')
        break
      case 'start-writing':
        navigate('/timecapsule')
        break
      case 'create-time-capsule':
        navigate('/timecapsule')
        break
      case 'view-memories':
        navigate('/stories')
        break
      case 'create-playlist':
        alert('Opening playlist creator...')
        break
      default:
        break
    }
  }

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto">
        {/* Hidden file input for photo uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Adaptive Welcome Banner based on user stage */}
        <WelcomeBanner
          stage={dashboardState.stage}
          showCelebration={dashboardState.showCelebrationBanner}
          metrics={dashboardState.metrics}
          greeting={greeting}
        />

        {/* Responsive Grid Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr_280px] lg:grid-cols-1 gap-4 sm:gap-6 lg:gap-8 min-h-[600px]">
          {/* Left Panel: Quick Actions */}
          <aside className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-xl p-4 sm:p-6 h-fit order-1 xl:order-1 lg:order-2">
            <h2 className="text-lg sm:text-xl font-semibold text-[#556B2F] mb-4 sm:mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3 sm:gap-4">
              <button 
                onClick={() => handleActionButton('record-story')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#556B2F] text-white hover:bg-[#6B8E23] hover:-translate-y-0.5"
              >
                🎤 Record a Story
              </button>
              <button 
                onClick={() => handleActionButton('add-family-member')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#6B8E23] text-white hover:bg-[#556B2F] hover:-translate-y-0.5"
              >
                👨‍👩‍👧‍👦 Add Family Member
              </button>
              <button 
                onClick={() => handleActionButton('upload-photos')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#556B2F] text-white hover:bg-[#6B8E23] hover:-translate-y-0.5"
              >
                📸 Upload Photos
              </button>
              <button 
                onClick={() => handleActionButton('share-recipe')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#6B8E23] text-white hover:bg-[#556B2F] hover:-translate-y-0.5"
              >
                🍳 Share a Recipe
              </button>
              <button 
                onClick={() => handleActionButton('create-wisdom-playlist')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#E65100] text-white hover:bg-[#FF7043] hover:-translate-y-0.5 col-span-2 xl:col-span-1"
              >
                💡 Create Wisdom Playlist
              </button>
            </div>
            
            <div className="mt-6 sm:mt-8">
              <h3 className="text-[#556B2F] mb-4 text-base sm:text-lg">Recent Activity</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 border-b border-[#CCCCCC]">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#6B8E23] flex items-center justify-center text-white text-sm sm:text-base">📝</div>
                  <div className="flex-1">
                    <div className="font-medium text-[#333333] text-sm sm:text-base">Added 3 photos</div>
                    <div className="text-xs sm:text-sm text-[#666666]">2 hours ago</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 border-b border-[#CCCCCC]">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#6B8E23] flex items-center justify-center text-white text-sm sm:text-base">🎙️</div>
                  <div className="flex-1">
                    <div className="font-medium text-[#333333] text-sm sm:text-base">Recorded a story</div>
                    <div className="text-xs sm:text-sm text-[#666666]">Yesterday</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#6B8E23] flex items-center justify-center text-white text-sm sm:text-base">👥</div>
                  <div className="flex-1">
                    <div className="font-medium text-[#333333] text-sm sm:text-base">Updated family tree</div>
                    <div className="text-xs sm:text-sm text-[#666666]">3 days ago</div>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Center Panel: Memory Highlights & Prompt */}
          <section className="flex flex-col gap-6 sm:gap-8 order-2 xl:order-2 lg:order-1">
            <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-[#556B2F] mb-4 sm:mb-6">Memory Highlights</h2>

              {/* Empty State - No stories yet */}
              {dashboardState.isEmpty && (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-lg font-semibold text-[#556B2F] mb-2">Your Story Collection Awaits</h3>
                  <p className="text-[#666666] mb-6 max-w-md mx-auto">
                    Start preserving your family's precious memories. Record your first story, share a cherished photo, or capture a favorite recipe.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => handleActionButton('record-story')}
                      className="px-6 py-3 bg-[#556B2F] text-white rounded-lg font-medium hover:bg-[#6B8E23] transition-colors"
                    >
                      Record Your First Story
                    </button>
                    <button
                      onClick={() => handleActionButton('upload-photos')}
                      className="px-6 py-3 bg-[#6B8E23] text-white rounded-lg font-medium hover:bg-[#556B2F] transition-colors"
                    >
                      Upload Photos
                    </button>
                  </div>
                </div>
              )}

              {/* First Story State - Show single card + suggestions */}
              {dashboardState.isNewUser && recentStories.length > 0 && (
                <div>
                  <div className="mb-6">
                    <div
                      onClick={() => navigate(`/stories/${recentStories[0].id}`)}
                      className="bg-white rounded-lg overflow-hidden shadow-md transition-transform cursor-pointer hover:-translate-y-1 border-l-4 border-[#E65100]"
                    >
                      <div className="p-4 sm:p-6">
                        <h3 className="font-semibold text-[#333333] text-lg mb-2">{recentStories[0].title}</h3>
                        <p className="text-[#666666] text-sm mb-3">
                          {recentStories[0].story_type || recentStories[0].event_data?.story_type || 'Story'}
                        </p>
                        <span className="text-[#E65100] font-medium">View your story →</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-[#E8F5E8] to-[#F1F8E9] rounded-lg p-4">
                    <h4 className="font-semibold text-[#556B2F] mb-3">Continue Building Your Legacy</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {dashboardState.suggestedActions.includes('upload_photos') && (
                        <button
                          onClick={() => handleActionButton('upload-photos')}
                          className="px-4 py-2 bg-white text-[#556B2F] rounded-lg text-sm font-medium hover:bg-[#556B2F] hover:text-white transition-colors border border-[#556B2F]"
                        >
                          Add photos to your story
                        </button>
                      )}
                      {dashboardState.suggestedActions.includes('add_family') && (
                        <button
                          onClick={() => handleActionButton('add-family-member')}
                          className="px-4 py-2 bg-white text-[#556B2F] rounded-lg text-sm font-medium hover:bg-[#556B2F] hover:text-white transition-colors border border-[#556B2F]"
                        >
                          Invite family members
                        </button>
                      )}
                      <button
                        onClick={() => handleActionButton('record-story')}
                        className="px-4 py-2 bg-white text-[#556B2F] rounded-lg text-sm font-medium hover:bg-[#556B2F] hover:text-white transition-colors border border-[#556B2F]"
                      >
                        Record another story
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Active/Mature State - Show memory grid */}
              {(dashboardState.isActive || dashboardState.isMature) && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
                    {recentStories.slice(0, 6).map((story, index) => (
                      <div
                        key={story.id || index}
                        onClick={() => navigate(`/stories/${story.id}`)}
                        className="bg-white rounded-lg overflow-hidden shadow-md transition-transform cursor-pointer hover:-translate-y-1"
                      >
                        <div className="w-full h-24 sm:h-30 bg-gradient-to-br from-[#E8F5E8] to-[#C8E6C9] flex items-center justify-center text-2xl sm:text-4xl text-[#556B2F]">
                          {getStoryEmoji(story.story_type || story.event_data?.story_type)}
                        </div>
                        <div className="p-3 sm:p-4">
                          <div className="font-semibold text-[#333333] mb-2 text-sm sm:text-base line-clamp-1">{story.title}</div>
                          <div className="text-xs sm:text-sm text-[#666666]">
                            {story.story_type || story.event_data?.story_type || 'Story'}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Fill with placeholder cards if less than 6 stories */}
                    {recentStories.length < 6 && [...Array(6 - recentStories.length)].map((_, i) => (
                      <div
                        key={`placeholder-${i}`}
                        onClick={() => handleActionButton('record-story')}
                        className="bg-white rounded-lg overflow-hidden shadow-md transition-transform cursor-pointer hover:-translate-y-1 border-2 border-dashed border-[#CCCCCC]"
                      >
                        <div className="w-full h-24 sm:h-30 bg-gradient-to-br from-[#F5F5F5] to-[#EEEEEE] flex items-center justify-center text-2xl sm:text-4xl text-[#CCCCCC]">+</div>
                        <div className="p-3 sm:p-4">
                          <div className="font-semibold text-[#999999] mb-2 text-sm sm:text-base">Add a Story</div>
                          <div className="text-xs sm:text-sm text-[#CCCCCC]">Share a memory</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => handleActionButton('view-memories')}
                    className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#6B8E23] text-white hover:bg-[#556B2F] hover:-translate-y-0.5"
                  >
                    View All Memories
                  </button>
                </>
              )}
            </div>

            {/* Recent Stories Section - Only show for active/mature users with multiple stories */}
            {(dashboardState.isActive || dashboardState.isMature) && recentStories.length > 1 && (
              <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-xl p-4 sm:p-6 mt-6 sm:mt-8">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-[#556B2F]">Recent Family Stories</h2>
                  <button 
                    onClick={() => navigate('/stories')}
                    className="text-sm text-[#556B2F] hover:text-[#6B8E23] font-medium hover:underline transition-colors"
                  >
                    View All →
                  </button>
                </div>
                
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
                  </div>
                ) : error ? (
                  <div className="text-center py-4 text-red-600 text-sm">
                    {error}
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {recentStories.map((story) => (
                      <div 
                        key={story.id}
                        onClick={() => navigate(`/stories/${story.id}`)}
                        className="bg-white rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition-all cursor-pointer hover:-translate-y-0.5 border-l-4 border-[#E65100]"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-[#333333] text-sm sm:text-base mb-1 line-clamp-1">
                              {story.title}
                            </h3>
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-[#666666] mb-2">
                              <span className="bg-[#E8F5E8] text-[#2D5016] px-2 py-1 rounded-full">
                                {story.story_type || story.event_data?.story_type || 'Story'}
                              </span>
                              <span>•</span>
                              <span>{new Date(story.date || story.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="text-[#E65100] text-lg">→</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-[#556B2F] mb-4 sm:mb-6">Today's Storytelling Prompt</h2>
              <div className="bg-gradient-to-br from-[#E8F5E8] to-[#F1F8E9] border-l-4 border-[#556B2F] p-4 sm:p-6 rounded-lg mb-6">
                <div className="text-base sm:text-lg text-[#333333] italic mb-2">
                  "What was your first job, and what did you learn from it?"
                </div>
                <div className="text-xs sm:text-sm text-[#666666]">Career & Life Lessons</div>
              </div>
              <button 
                onClick={() => handleActionButton('answer-prompt')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#556B2F] text-white hover:bg-[#6B8E23] hover:-translate-y-0.5"
              >
                Answer This Prompt
              </button>
            </div>
          </section>

          {/* Right Panel: Legacy Features */}
          <aside className="flex flex-col gap-6 sm:gap-8 order-3 xl:order-3 lg:order-3">
            <div className="bg-gradient-to-br from-[#FFF3E0] to-[#FFE0B2] border border-[#FFB74D] rounded-xl p-4 sm:p-6 text-center">
              <div className="text-4xl sm:text-5xl mb-4">💌</div>
              <h3 className="text-base sm:text-lg font-semibold text-[#333333] mb-2">Legacy Letters</h3>
              <p className="text-[#666666] mb-6 text-sm sm:text-base">Write a letter to future generations</p>
              <button 
                onClick={() => handleActionButton('start-writing')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#E65100] text-white hover:bg-[#FF7043] hover:-translate-y-0.5"
              >
                Start Writing
              </button>
            </div>

            <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-xl p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-semibold text-[#556B2F] mb-4 sm:mb-6">Wisdom Playlists</h3>
              <div className="text-center p-4">
                <div className="text-3xl sm:text-4xl mb-4">🎵</div>
                <p className="text-[#666666] mb-4 text-sm sm:text-base">Organize your life lessons into themed collections</p>
                <button 
                  onClick={() => handleActionButton('create-playlist')}
                  className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#6B8E23] text-white hover:bg-[#556B2F] hover:-translate-y-0.5"
                >
                  Create Playlist
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] border border-[#64B5F6] rounded-xl p-4 sm:p-6 text-center">
              <div className="text-3xl sm:text-4xl mb-4">⏰</div>
              <h3 className="text-base sm:text-lg font-semibold text-[#333333] mb-2">Time Capsules</h3>
              <p className="text-[#666666] mb-4 text-sm sm:text-base">Send messages to the future</p>
              <button 
                onClick={() => handleActionButton('create-time-capsule')}
                className="w-full p-3 sm:p-4 border-none rounded-lg text-sm sm:text-base font-medium cursor-pointer transition-all flex items-center justify-center gap-2 bg-[#556B2F] text-white hover:bg-[#6B8E23] hover:-translate-y-0.5"
              >
                Create Time Capsule
              </button>
            </div>
          </aside>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8">
          <div className="bg-white border border-[#CCCCCC] rounded-lg p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#556B2F]">
              {loading ? '...' : stats.totalStories}
            </div>
            <div className="text-[#666666] text-xs sm:text-sm">Stories Shared</div>
          </div>
          <div className="bg-white border border-[#CCCCCC] rounded-lg p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#556B2F]">
              {loading ? '...' : stats.withMedia}
            </div>
            <div className="text-[#666666] text-xs sm:text-sm">With Media</div>
          </div>
          <div className="bg-white border border-[#CCCCCC] rounded-lg p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#556B2F]">
              {loading ? '...' : stats.totalLifeEvents}
            </div>
            <div className="text-[#666666] text-xs sm:text-sm">Life Events</div>
          </div>
          <div className="bg-white border border-[#CCCCCC] rounded-lg p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#556B2F]">
              {loading ? '...' : stats.thisMonth}
            </div>
            <div className="text-[#666666] text-xs sm:text-sm">This Month</div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Dashboard
